import {
  Fragment,
  useCallback,
  useMemo,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useDndContext } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { isChoiceFieldType } from "@salestrak/custom-fields";
import {
  Badge,
  Box,
  Button,
  Collapsible,
  CollapsiblePanel,
  DND_DRAGGABLE_PROPS,
  DND_OVERLAY_PROPS,
  sortableStyle,
  EditableTitleField,
  Icon,
  Menu,
  MenuPopup,
  MenuTrigger,
  Text,
  type TextProps,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  usePrefersReducedMotion,
} from "@salestrak/ui";

import { getOptionMetas } from "#app/components/custom-fields/option-visuals";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import {
  PageLine,
  SectionSplitHint,
  SectionTab,
} from "#app/components/forms/form-builder/form-sections";
import {
  SORT_TRANSITION,
  cardOutlineData,
  useCanvasDropRegion,
  useFormDragState,
  useInsertSeamDrop,
  type CardOutline,
} from "#app/components/forms/form-builder/form-builder-dnd";
import {
  CANVAS_ITEM_ATTRIBUTE,
  type CanvasDensity,
} from "#app/components/forms/form-builder/form-canvas-zoom";
import {
  BRANCH_HUE,
  BRANCH_INDENT,
  BranchLine,
  ConditionTab,
  describeCondition,
  type ConditionDescription,
} from "#app/components/forms/form-builder/form-conditions";
import {
  ItemDestination,
  formItemGlyph,
} from "#app/components/forms/form-builder/form-item-identity";
import { type CreateFieldDoor } from "#app/components/forms/form-builder/form-answer-link";
import { InsertMenuItems } from "#app/components/forms/form-builder/form-item-palette";
import { FormFillItem } from "#app/components/forms/form-fill/form-fill-body";
import {
  FormOptionChip,
  formOptionProperty,
  useFormOptionEntity,
} from "#app/components/forms/form-property-options";
import {
  canMoveSection,
  formItemNotice,
  formOutline,
  formSteps,
  defaultValueError,
  initialFormOnlyAnswer,
  linkedDefaultAnswer,
  isFieldItem,
  longPageSplit,
  resolveItemField,
  type Form,
  type FormCondition,
  type FormItem,
} from "#app/lib/forms";

/** The insert slot IS this gap, so its "+" sits on the seam where the pointer meets it between cards. */
export const SEAM_HEIGHT: Record<CanvasDensity, number> = { compact: 36, full: 40, overview: 8 };

const TRAILING_SEAM_HEIGHT = 16;

const SUMMARY_OPTIONS = 6;

const NO_ANSWERS: Record<string, unknown> = {};

const STRANDED_MOVE = "A conditional question would end up above the question it follows";

function canvasItemName(
  item: FormItem,
  customFieldsById: ReadonlyMap<string, CustomFieldDef>,
): { label: string; placeholder: boolean } {
  if (item.kind === "section") {
    return item.label
      ? { label: item.label, placeholder: false }
      : { label: "Untitled section", placeholder: true };
  }
  if (item.kind === "text") {
    const line = item.label || item.body.split("\n")[0];
    return line ? { label: line, placeholder: false } : { label: "Empty text block", placeholder: true };
  }
  const label = item.label || resolveItemField(item, customFieldsById)?.name;
  return label ? { label, placeholder: false } : { label: "Untitled question", placeholder: true };
}

type CardRename = {
  armed: boolean;
  editing: boolean;
  onStart: () => void;
  onCommit: (label: string) => void;
  onEnd: () => void;
};

/** The press is kept from a surrounding label element: its activation would pass the click to the answer control. */
function CardName({
  item,
  customFieldsById,
  size,
  weight,
  truncate = false,
  multiline = false,
  required = false,
  rename,
}: {
  item: FormItem;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  size: TextProps["size"];
  weight: TextProps["weight"];
  truncate?: TextProps["truncate"];
  multiline?: boolean;
  required?: boolean;
  rename: CardRename | undefined;
}) {
  const { label, placeholder } = canvasItemName(item, customFieldsById);
  const mark = required ? (
    <>
      <Text aria-hidden size={size} variant="destructive" weight={weight}>
        *
      </Text>
      <Text visuallyHidden> (required)</Text>
    </>
  ) : undefined;
  const editing = rename?.armed === true && rename.editing;
  return (
    // A flex row, never an inline span: an inline box ignores `truncate`'s overflow + ellipsis.
    <Box
      as="span"
      direction="row"
      gap="2xs"
      grow={editing}
      minWidth={0}
      onClick={(event: MouseEvent<HTMLElement>) => event.preventDefault()}
    >
      {rename?.armed ? (
        <EditableTitleField
          aria-label={`Rename ${label}`}
          chrome="wash"
          editing={rename.editing}
          fill={editing}
          multiline={multiline}
          onCommit={rename.onCommit}
          onEditingChange={(next) => (next ? rename.onStart() : rename.onEnd())}
          placeholder={canvasItemName({ ...item, label: "" }, customFieldsById).label}
          restAs="text"
          size={size}
          trailing={mark}
          truncate={truncate}
          value={placeholder ? "" : label}
          weight={weight}
        />
      ) : (
        <>
          <Text size={size} truncate={truncate} variant={placeholder ? "faint" : undefined} weight={weight}>
            {label}
          </Text>
          {mark}
        </>
      )}
    </Box>
  );
}

/** The answer is `inert`: the control is a picture of the question, so a press lands on the card. */
function ItemPreview({
  item,
  customFieldsById,
  rename,
}: {
  item: FormItem;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  rename: CardRename | undefined;
}) {
  // A linked answer's start is the Project's, unknown here, so a linked choice shows only the form's own default.
  const answers = useMemo(() => {
    if (item.kind === "question" || item.kind === "attachment") {
      return { [item.itemId]: initialFormOnlyAnswer(item) };
    }
    if (item.kind === "customField" || item.kind === "property") {
      const defaults = linkedDefaultAnswer(item, resolveItemField(item, customFieldsById));
      return defaults ? { [item.itemId]: defaults } : NO_ANSWERS;
    }
    return NO_ANSWERS;
  }, [customFieldsById, item]);
  if (item.kind === "text" && !item.body && !item.label) {
    return (
      <Text size="sm" variant="faint">
        Empty text block
      </Text>
    );
  }
  const name =
    item.kind === "text" ? undefined : (
      <CardName
        customFieldsById={customFieldsById}
        item={item}
        multiline
        rename={rename}
        required={isFieldItem(item) && item.required}
        size={item.kind === "section" ? "lg" : "sm"}
        weight={item.kind === "section" ? "semibold" : "medium"}
      />
    );
  return (
    <FormFillItem
      customFieldsById={customFieldsById}
      error={undefined}
      inertAnswer
      item={item}
      // A typed answer's input reads its value once, so a new default remounts it.
      key={JSON.stringify(answers[item.itemId] ?? null)}
      label={name}
      onAnswerChange={() => undefined}
      value={answers[item.itemId]}
    />
  );
}

function ItemSummary({
  item,
  customFieldsById,
  rename,
}: {
  item: FormItem;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  rename: CardRename | undefined;
}) {
  const { label, placeholder } = canvasItemName(item, customFieldsById);
  const entityOf = useFormOptionEntity(formOptionProperty(item));
  const ink = placeholder ? "faint" : undefined;
  if (item.kind === "section") {
    return (
      <Box direction="column" gap="2xs">
        <CardName
          customFieldsById={customFieldsById}
          item={item}
          multiline
          rename={rename}
          size="base"
          truncate={2}
          weight="semibold"
        />
        {item.description ? (
          <Text size="sm" truncate={1} variant="muted">
            {item.description}
          </Text>
        ) : null}
      </Box>
    );
  }
  if (item.kind === "text") {
    return item.label && item.body ? (
      <Box direction="column" gap="2xs">
        <Text size="sm" truncate={1} weight="semibold">
          {item.label}
        </Text>
        <Text size="sm" truncate={2} variant="muted">
          {item.body}
        </Text>
      </Box>
    ) : (
      <Text size="sm" truncate={3} variant={ink} weight={item.label ? "semibold" : undefined}>
        {item.body || label}
      </Text>
    );
  }
  const field = resolveItemField(item, customFieldsById);
  const options =
    field && isChoiceFieldType(field.type) ? getOptionMetas(field) : [];
  const description = isFieldItem(item) ? item.description : undefined;
  return (
    <Box direction="column" gap="sm">
      <Box direction="column" gap="2xs">
        <CardName
          customFieldsById={customFieldsById}
          item={item}
          multiline
          rename={rename}
          required={isFieldItem(item) && item.required}
          size="sm"
          truncate={2}
          weight="medium"
        />
        {description ? (
          <Text size="xs" truncate={1} variant="muted">
            {description}
          </Text>
        ) : null}
      </Box>
      {options.length > 0 ? (
        <Box align="center" direction="row" gap="xs" wrap>
          {options.slice(0, SUMMARY_OPTIONS).map((option) => (
            <FormOptionChip
              entity={entityOf(option.value)}
              key={option.value}
              label={option.label}
            />
          ))}
          {options.length > SUMMARY_OPTIONS ? (
            <Text size="xs" variant="muted">
              +{options.length - SUMMARY_OPTIONS} more
            </Text>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

/** Acknowledges the press on mousedown (`pressFeedback`): selection lands a re-render later and dnd-kit paints nothing before the 8px threshold. */
function ItemCard({
  item,
  selected,
  folded,
  outline,
  followUps,
  density,
  customFieldsById,
  onSelect,
  onRename,
  onDuplicate,
  onRemove,
}: {
  item: FormItem;
  selected: boolean;
  density: CanvasDensity;
  onDuplicate: (itemId: string) => void;
  onRemove: (itemId: string) => void;
  folded: boolean;
  outline: CardOutline;
  followUps: number;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  onSelect: (itemId: string) => void;
  onRename: (itemId: string, label: string) => void;
}) {
  const reducedMotion = usePrefersReducedMotion();
  const [editing, setEditing] = useState(false);
  const renamable = item.kind !== "text";
  const armed = renamable && selected && !folded;
  if (editing && !armed) setEditing(false);
  const renaming = editing && armed;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    data: cardOutlineData(outline),
    disabled: folded ? { draggable: true, droppable: true } : false,
    id: item.itemId,
    transition: reducedMotion ? null : SORT_TRANSITION,
  });
  const nodeRef = useCallback(
    (node: unknown) => setNodeRef(node as HTMLElement | null),
    [setNodeRef],
  );
  const glyph = formItemGlyph(item, customFieldsById);

  const select = () => {
    // isDragging is still true when the browser fires the drop's click.
    if (!isDragging) onSelect(item.itemId);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      select();
    }
  };
  const rename: CardRename | undefined = renamable
    ? {
        armed,
        editing: renaming,
        onCommit: (label) => onRename(item.itemId, label),
        onEnd: () => setEditing(false),
        onStart: () => {
          if (!isDragging) setEditing(true);
        },
      }
    : undefined;

  return (
    // Quick actions are siblings of the card, never inside it: a button in the card's button nests controls.
    <Box
      ref={nodeRef}
      group="card"
      opacity={isDragging ? 30 : undefined}
      position="relative"
      style={sortableStyle(transform, { transition, isSorting })}
    >
      <Box
        touchAction="manipulation"
        {...DND_DRAGGABLE_PROPS}
        {...(attributes as object)}
        // Renaming drops the activators, never the draggable: a disabled one marks the card `aria-disabled`, which disables the field inside.
        {...((renaming ? {} : listeners) as object)}
        aria-pressed={selected}
        background="elevated"
        border
        borderColor={selected ? "ring" : undefined}
        cursor={isDragging ? "grabbing" : renaming ? "pointer" : "grab"}
        direction="column"
        onClick={select}
        onKeyDown={onKeyDown}
        overflow="clip"
        position="relative"
        pressFeedback={!isDragging && !renaming}
        rounded="lg"
        shadow={selected ? "sm" : undefined}
      >
        <ItemCardFace
          customFieldsById={customFieldsById}
          density={density}
          followUps={followUps}
          glyph={glyph}
          grip={!renaming}
          gripRevealed={selected}
          item={item}
          rename={rename}
        />
      </Box>
      {renaming || isDragging ? null : (
        <CardActions
          density={density}
          name={canvasItemName(item, customFieldsById).label}
          // A linked question's field is on the form once; a copy would link it twice.
          onDuplicate={
            item.kind === "customField" || item.kind === "property" ? undefined : () => onDuplicate(item.itemId)
          }
          onRemove={() => onRemove(item.itemId)}
          revealed={selected}
        />
      )}
    </Box>
  );
}

const CARD_ACTIONS_WIDTH = 58;

export const STRIP_HEIGHT: Record<CanvasDensity, number> = { compact: 36, full: 40, overview: 36 };

export const STRIP_INSET = {
  compact: { end: "sm", start: "md" },
  full: { end: "md", start: "lg" },
  overview: { end: "md", start: "lg" },
} as const satisfies Record<CanvasDensity, { end: string; start: string }>;

function CardActions({
  density,
  name,
  revealed,
  onDuplicate,
  onRemove,
}: {
  density: CanvasDensity;
  name: string;
  revealed: boolean;
  onDuplicate: (() => void) | undefined;
  onRemove: () => void;
}) {
  const stop = (event: { stopPropagation: () => void }) => event.stopPropagation();
  return (
    <Box
      align="center"
      direction="row"
      gap="2xs"
      height={STRIP_HEIGHT[density]}
      hoverReveal={revealed ? undefined : "card"}
      onClick={stop}
      onKeyDown={stop}
      onPointerDown={stop}
      paddingRight={STRIP_INSET[density].end}
      position="absolute"
      revealOnTouch
      right={1}
      top={1}
    >
      {onDuplicate ? <CardActionButton icon="copy" label="Duplicate" name={name} onClick={onDuplicate} /> : null}
      <CardActionButton icon="trash" label="Remove" name={name} onClick={onRemove} />
    </Box>
  );
}

function CardActionButton({
  icon,
  label,
  name,
  onClick,
}: {
  icon: "copy" | "trash";
  label: string;
  name: string;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button aria-label={`${label} ${name}`} onClick={onClick} size="icon-sm" variant="ghost">
            <Icon color="muted" name={icon} size="xs" />
          </Button>
        }
      />
      <TooltipPopup>{label}</TooltipPopup>
    </Tooltip>
  );
}

function ItemCardFace({
  item,
  glyph,
  grip,
  gripRevealed,
  followUps,
  density,
  customFieldsById,
  rename,
}: {
  item: FormItem;
  glyph: ReturnType<typeof formItemGlyph>;
  grip: boolean;
  gripRevealed: boolean;
  followUps: number;
  density: CanvasDensity;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  rename?: CardRename | undefined;
}) {
  const required = isFieldItem(item) && item.required;
  // Facts read after the item's identity, never at the trailing edge: that edge belongs to the quick actions.
  const dot = (
    <Text aria-hidden size="xs" variant="faint">
      ·
    </Text>
  );
  const notice = formItemNotice(item, customFieldsById);
  const badDefault =
    (item.kind === "question" || item.kind === "attachment") && defaultValueError(item) !== null;
  const facts = (
    <>
      {glyph.linkBroken || glyph.linkedTo ? (
        <>
          {dot}
          <ItemDestination glyph={glyph} />
        </>
      ) : null}
      {badDefault ? (
        <>
          {dot}
          <Badge color="destructive" tone="soft">
            <Icon name="alert-triangle" size="xs" />
            Invalid default
          </Badge>
        </>
      ) : null}
      {notice ? (
        <>
          {dot}
          <Tooltip>
            <TooltipTrigger render={<Badge color="warning" tone="soft" />}>
              <Icon name="alert-triangle" size="xs" />
              {notice.kind === "hidden" ? "Not shown" : "Rule ignored"}
            </TooltipTrigger>
            <TooltipPopup>{notice.reason}</TooltipPopup>
          </Tooltip>
        </>
      ) : null}
      {followUps > 0 ? (
        <>
          {dot}
          <Box align="center" direction="row" gap="xs" shrink={false}>
            <Icon color={BRANCH_HUE} name="git-branch" size="xs" />
            <Text size="xs" variant="muted">
              {`${followUps} conditional`}
            </Text>
          </Box>
        </>
      ) : null}
    </>
  );

  if (density === "overview") {
    return (
      <Box
        align="center"
        direction="row"
        gap="sm"
        minHeight={STRIP_HEIGHT.overview}
        paddingLeft={STRIP_INSET.overview.start}
        paddingRight={STRIP_INSET.overview.end}
        position="relative"
      >
        {grip ? (
          <Box
            align="center"
            hoverReveal={gripRevealed ? undefined : "card"}
            insetY={0}
            justify="center"
            left={0}
            pointerEvents="none"
            position="absolute"
            width={24}
          >
            <Icon color="faint" name="grip-vertical" size="md" />
          </Box>
        ) : null}
        <Icon color={glyph.color} name={glyph.icon} size="sm" />
        <Box align="center" direction="row" gap="xs" grow minWidth={0}>
          <CardName
            customFieldsById={customFieldsById}
            item={item}
            rename={rename}
            required={required}
            size="sm"
            truncate
            weight={item.kind === "section" ? "semibold" : "medium"}
          />
          {facts}
        </Box>
        <Box aria-hidden shrink={false} width={CARD_ACTIONS_WIDTH} />
      </Box>
    );
  }

  return (
    <>
      <Box
        align="center"
        border="bottom"
        direction="row"
        gap="sm"
        minHeight={STRIP_HEIGHT[density]}
        paddingLeft={STRIP_INSET[density].start}
        paddingRight={STRIP_INSET[density].end}
        position="relative"
      >
        <Box align="center" direction="row" gap="xs" maxWidth="calc(50% - 20px)" minWidth={0}>
          <Box align="center" direction="row" gap="xs" shrink={false}>
            <Icon color={glyph.color} name={glyph.icon} size="xs" />
            <Text size="xs" variant="muted">
              {glyph.kindLabel}
            </Text>
          </Box>
          {facts}
        </Box>
        {grip ? (
          <Box
            align="center"
            hoverReveal={gripRevealed ? undefined : "card"}
            insetX={0}
            justify="center"
            pointerEvents="none"
            position="absolute"
          >
            <Icon className="rotate-90" color="faint" name="grip-vertical" size="md" />
          </Box>
        ) : null}
      </Box>
      {density === "full" ? (
        <Box direction="column" gap="sm" padding="lg">
          <ItemPreview customFieldsById={customFieldsById} item={item} rename={rename} />
        </Box>
      ) : (
        <Box direction="column" padding="md">
          <ItemSummary customFieldsById={customFieldsById} item={item} rename={rename} />
        </Box>
      )}
    </>
  );
}

export function FormCardGhost({
  itemId,
  form,
  selected,
  density,
  customFieldsById,
}: {
  itemId: string;
  form: Pick<Form, "items">;
  selected: boolean;
  density: CanvasDensity;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
}) {
  const item = form.items.find((row) => row.itemId === itemId);
  const followUps = useMemo(
    () => formOutline(form.items).runOf(itemId).length,
    [form.items, itemId],
  );
  if (!item) return null;
  return (
    <Box
      {...DND_OVERLAY_PROPS}
      background="elevated"
      border
      borderColor={selected ? "ring" : undefined}
      cursor="grabbing"
      direction="column"
      overflow="clip"
      position="relative"
      rounded="lg"
      shadow="lg"
    >
      <ItemCardFace
        customFieldsById={customFieldsById}
        density={density}
        followUps={followUps}
        glyph={formItemGlyph(item, customFieldsById)}
        grip
        gripRevealed
        item={item}
      />
    </Box>
  );
}

/** `rails` names the lanes (1 = outermost) still open through this wrapper; a rail ends in the elbow of its run's LAST follow-up. */
const MAX_INDENT_LANES = 3;

function Indent({
  depth,
  rails,
  elbow = false,
  children,
}: {
  depth: number;
  rails: readonly number[];
  elbow?: boolean;
  children: ReactNode;
}) {
  let wrapped = rails.includes(depth + 1) && depth < MAX_INDENT_LANES ? (
    <Box position="relative">
      <BranchLine rail />
      {children}
    </Box>
  ) : (
    children
  );
  // Innermost lane first, so the last wrapper built is the outermost.
  for (let level = Math.min(depth, MAX_INDENT_LANES); level >= 1; level -= 1) {
    const rail = rails.includes(level);
    const branch = elbow && level === depth;
    wrapped = (
      <Box direction="row">
        <Box position="relative" shrink={false} width={BRANCH_INDENT}>
          {rail || branch ? <BranchLine elbow={branch} rail={rail} /> : null}
        </Box>
        <Box direction="column" grow minWidth={0}>
          {wrapped}
        </Box>
      </Box>
    );
  }
  return <>{wrapped}</>;
}

/** Inert to the pointer while any drag is in flight, so crossing seams mid-drag lights no hover chrome. */
function InsertSlot({
  index,
  disabled,
  inherit,
  height,
  form,
  trailing = false,
  onInsert,
  onCreateField,
}: {
  form: Pick<Form, "items">;
  trailing?: boolean;
  onCreateField: CreateFieldDoor;
  index: number;
  height: number;
  inherit: FormCondition | undefined;
  disabled: boolean;
  onInsert: (index: number, item: FormItem) => void;
}) {
  const { ref, isOver } = useInsertSeamDrop(index, inherit);
  const add = (item: FormItem) =>
    onInsert(index, inherit && item.kind !== "section" ? { ...item, showWhen: inherit } : item);
  const targeted = useFormDragState() === "palette" && isOver;
  return (
    <Box
      ref={ref}
      align="center"
      direction="row"
      gap="sm"
      group="gutter"
      height={trailing ? Math.min(height, TRAILING_SEAM_HEIGHT) : height}
      marginXNegative="base"
      pointerEvents={disabled ? "none" : undefined}
      position="relative"
      revealOnTouch
      zIndex={1}
    >
      {targeted ? (
        <Box background="primary" fill="flex" height={2} rounded="sm" />
      ) : trailing ? null : (
        <>
          <Box border="top" fill="flex" hoverReveal="gutter" />
          <Box hoverReveal="gutter" shrink={false}>
            <Menu>
              <MenuTrigger
                render={
                  <Button aria-label="Add a question here" size="icon-sm" variant="outline">
                    <Icon name="plus" size="md" />
                  </Button>
                }
              />
              <MenuPopup align="center" width="panel">
                <InsertMenuItems form={form} onAdd={add} onCreateField={onCreateField} />
              </MenuPopup>
            </Menu>
          </Box>
          <Box border="top" fill="flex" hoverReveal="gutter" />
        </>
      )}
    </Box>
  );
}

function AddQuestionCard({
  form,
  hidden,
  onInsert,
  onCreateField,
}: {
  form: Pick<Form, "items">;
  hidden: boolean;
  onInsert: (index: number, item: FormItem) => void;
  onCreateField: CreateFieldDoor;
}) {
  return (
    <Box
      background="elevated"
      border
      inert={hidden || undefined}
      opacity={hidden ? 0 : undefined}
      padding="md"
      rounded="lg"
    >
      <Menu>
        <MenuTrigger
          render={
            // Named for where it adds: the Conditions pane has its own "Add question" (a follow-up).
            <Button aria-label="Add question at the end" fullWidth shape="square" variant="outline">
              <Icon name="plus" size="md" />
              Add question
            </Button>
          }
        />
        <MenuPopup align="center" width="panel">
          <InsertMenuItems
            form={form}
            onAdd={(item) => onInsert(form.items.length, item)}
            onCreateField={onCreateField}
          />
        </MenuPopup>
      </Menu>
    </Box>
  );
}

/** The `DndContext` is the builder's (`FormBuilderDndScope`): a palette shape must cross columns, which is one drag space or none. */
export function FormCanvas({
  form,
  selectedItemId,
  density,
  customFieldsById,
  onSelect,
  onInsert,
  onCreateField,
  onMoveSection,
  onRename,
  onDuplicate,
  onRemove,
}: {
  form: Pick<Form, "name" | "items">;
  selectedItemId: string | null;
  density: CanvasDensity;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  onSelect: (itemId: string) => void;
  onInsert: (index: number, item: FormItem) => void;
  onCreateField: CreateFieldDoor;
  onMoveSection: (sectionItemId: string, by: -1 | 1) => void;
  onRename: (itemId: string, label: string) => void;
  onDuplicate: (itemId: string) => void;
  onRemove: (itemId: string) => void;
}) {
  // An empty form has no seams, so the region alone catches the drop and it lands at the top.
  const regionRef = useCanvasDropRegion();
  const dragging = useFormDragState() !== "none";
  const pages = useMemo(() => {
    const steps = formSteps(form);
    const stepOfItem = new Map<string, number>();
    const closesPage = new Set<string>();
    steps.forEach((step, index) => {
      for (const item of step.items) stepOfItem.set(item.itemId, index);
      const last = step.items.at(-1);
      if (last) closesPage.add(last.itemId);
    });
    return { closesPage, steps, stepOfItem };
  }, [form]);
  const paged = pages.steps.length > 1;
  const [hoveredPage, setHoveredPage] = useState<number | null>(null);
  const selectedPage = selectedItemId ? pages.stepOfItem.get(selectedItemId) : undefined;
  const split = useMemo(() => longPageSplit(form), [form]);
  const outline = useMemo(() => formOutline(form.items), [form.items]);
  const indexOf = useMemo(
    () => new Map(form.items.map((item, index) => [item.itemId, index] as const)),
    [form.items],
  );
  // While a card is in flight its run folds into it and leaves the sortable's index space and drop targets, so one block moves.
  const { active } = useDndContext();
  const liftedId = active && indexOf.has(String(active.id)) ? String(active.id) : null;
  const folded = useMemo(
    () => new Set(liftedId ? outline.runOf(liftedId) : []),
    [liftedId, outline],
  );
  const sortableIds = useMemo(
    () => form.items.filter((item) => !folded.has(item.itemId)).map((item) => item.itemId),
    [folded, form.items],
  );
  const runSizes = useMemo(() => {
    const sizes = new Map<string, number>();
    const shallower: number[] = [];
    for (let index = form.items.length - 1; index >= 0; index -= 1) {
      const id = form.items[index]!.itemId;
      const own = outline.depth.get(id) ?? 0;
      while (shallower.length > 0 && (outline.depth.get(form.items[shallower.at(-1)!]!.itemId) ?? 0) > own) {
        shallower.pop();
      }
      sizes.set(id, (shallower.at(-1) ?? form.items.length) - index - 1);
      shallower.push(index);
    }
    return sizes;
  }, [form.items, outline]);
  const nextStanding = useMemo(() => {
    const next = new Map<string, FormItem>();
    let standing: FormItem | undefined;
    for (let index = form.items.length - 1; index >= 0; index -= 1) {
      const item = form.items[index]!;
      if (standing) next.set(item.itemId, standing);
      if (!folded.has(item.itemId)) standing = item;
    }
    return next;
  }, [folded, form.items]);
  // Built once for the column: a rule reads the questions above its card, so per-card description would walk the list per card.
  const conditionChips = useMemo(() => {
    const chips = new Map<string, ConditionDescription>();
    for (const item of form.items) {
      if (item.kind === "section" || !item.showWhen) continue;
      const described = describeCondition(
        item.showWhen,
        form.items,
        item.itemId,
        customFieldsById,
      );
      if (described) chips.set(item.itemId, described);
    }
    return chips;
  }, [customFieldsById, form.items]);

  const seamHeight = SEAM_HEIGHT[density];

  if (form.items.length === 0) {
    return (
      <Box ref={regionRef} direction="column">
        <Box height={seamHeight} />
        <AddQuestionCard
          form={form}
          hidden={false}
          onCreateField={onCreateField}
          onInsert={onInsert}
        />
      </Box>
    );
  }

  return (
    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
      <Box ref={regionRef} aria-label="Form items" direction="column" role="list">
        <InsertSlot
          disabled={dragging}
          height={seamHeight}
          index={0}
          form={form}
          inherit={undefined}
          onCreateField={onCreateField}
          onInsert={onInsert}
        />
        {form.items.map((item, index) => {
          const isFolded = folded.has(item.itemId);
          const page = pages.stepOfItem.get(item.itemId) ?? 0;
          const depth = outline.depth.get(item.itemId) ?? 0;
          // A folded card is not there to lead into, so a rail never points at a follow-up in flight.
          const next = nextStanding.get(item.itemId);
          const nextDepth = next ? (outline.depth.get(next.itemId) ?? 0) : 0;
          const chip = conditionChips.get(item.itemId);
          const through = outline.through.get(item.itemId) ?? [];
          const cardRails =
            depth > 0 && !chip && !through.includes(depth) ? [...through, depth] : through;
          const seamRails = nextDepth === depth + 1 ? [...through, depth + 1] : through;
          const controllerId = item.kind === "section" ? undefined : item.showWhen?.whenItemId;
          const controllerIndex =
            controllerId === undefined ? null : (indexOf.get(controllerId) ?? null);
          // Next card deeper: the seam lies INSIDE that deeper run, so the shape takes the follow-up's rule; a sibling of this card would split the run.
          const inRun = next && nextDepth > depth ? next : item;
          const seamInherit =
            inRun.kind !== "section" && (inRun === next || depth > 0) ? inRun.showWhen : undefined;
          return (
            <Fragment key={item.itemId}>
              {!dragging && split?.seat === index ? (
                <Box paddingBottom="md">
                  <SectionSplitHint onInsert={onInsert} split={split} />
                </Box>
              ) : null}
              {/* Root renders `display: contents` so the PANEL is the line's child: a folded card holds no slot once gone. */}
              <PageLine
                end={pages.closesPage.has(item.itemId)}
                endGap={next ? seamHeight : Math.min(seamHeight, TRAILING_SEAM_HEIGHT)}
                on={paged}
                start={item.kind === "section" || index === 0}
                onHover={(inside) =>
                  setHoveredPage((current) => (inside ? page : current === page ? null : current))
                }
              >
                <Collapsible open={!isFolded} render={<Box display="contents" />}>
                  <CollapsiblePanel bleed="sm" role="listitem">
                    <Indent depth={depth} elbow={Boolean(chip) && depth > 0} rails={cardRails}>
                      <Box
                        armOnHold={false}
                        direction="column"
                        group={item.kind === "section" ? "section" : undefined}
                        {...{ [CANVAS_ITEM_ATTRIBUTE]: item.itemId }}
                      >
                        {paged && index === 0 && item.kind !== "section" ? (
                          <SectionTab index={0} total={pages.steps.length} />
                        ) : null}
                        {paged && item.kind === "section" ? (
                          <SectionTab
                            index={page}
                            revealed={hoveredPage === page || selectedPage === page}
                            onMove={{
                              down: pages.steps[page + 1]
                                ? canMoveSection(form.items, item.itemId, 1)
                                  ? () => onMoveSection(item.itemId, 1)
                                  : STRANDED_MOVE
                                : null,
                              up: pages.steps[page - 1]?.section
                                ? canMoveSection(form.items, item.itemId, -1)
                                  ? () => onMoveSection(item.itemId, -1)
                                  : STRANDED_MOVE
                                : null,
                            }}
                            total={pages.steps.length}
                          />
                        ) : null}
                        {/* A rule that can no longer be read states nothing: the question shows unconditionally. */}
                        {chip ? <ConditionTab description={chip} /> : null}
                        <ItemCard
                          customFieldsById={customFieldsById}
                          density={density}
                          folded={isFolded}
                          followUps={runSizes.get(item.itemId) ?? 0}
                          item={item}
                          onDuplicate={onDuplicate}
                          onRemove={onRemove}
                          onRename={onRename}
                          onSelect={onSelect}
                          outline={{
                            controllerIndex,
                            depth,
                            index,
                            parentId: outline.parentId.get(item.itemId) ?? null,
                          }}
                          selected={item.itemId === selectedItemId}
                        />
                      </Box>
                    </Indent>
                    <Indent depth={depth} rails={seamRails}>
                      <InsertSlot
                        disabled={dragging}
                        height={seamHeight}
                        index={index + 1}
                        form={form}
                        inherit={seamInherit}
                        onCreateField={onCreateField}
                        onInsert={onInsert}
                        trailing={!next}
                      />
                    </Indent>
                  </CollapsiblePanel>
                </Collapsible>
              </PageLine>
            </Fragment>
          );
        })}
        <AddQuestionCard
          form={form}
          hidden={dragging}
          onCreateField={onCreateField}
          onInsert={onInsert}
        />
      </Box>
    </SortableContext>
  );
}
